import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { PRODUCTS, IMAGES, formatINR, getProductVariantPrice } from "@/lib/products";
import { loadCatalogProducts } from "@/lib/catalog";
import { ProductCard } from "@/components/product/ProductCard";
import { useCart } from "@/context/CartContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RotateCcw, ChevronRight, ChevronDown, Star, ArrowRight, Check } from "lucide-react";

const CATS = [
  { key: "all", label: "All Planners" },
  { key: "planners", label: "Planners" },
];

const TYPES = ["daily", "weekly", "undated", "academic", "mba"];
const FORMATS = ["A5", "B5"];
const COVERS = ["Soft Paperback", "Hard Binding"];
const FEATURES = ["undated", "goal-setting", "habit-tracking"];

export default function Shop() {
  const [params] = useSearchParams();
  const initialCat = params.get("cat") || "all";

  const [cat, setCat] = useState(initialCat);
  const [types, setTypes] = useState([]);
  const [formats, setFormats] = useState([]);
  const [covers, setCovers] = useState([]);
  const [features, setFeatures] = useState([]);
  const [price, setPrice] = useState([0, 12000]);
  const [sort, setSort] = useState("featured");
  const [products, setProducts] = useState(PRODUCTS);
  const { addItem } = useCart();
  const navigate = useNavigate();
  const bestseller = products.find((p) => p.slug === "life-os-planner" || p.id === "life-os-planner");

  useEffect(() => {
    let mounted = true;
    loadCatalogProducts().then((loaded) => {
      if (mounted) setProducts(loaded);
    });
    return () => { mounted = false; };
  }, []);

  const toggle = (arr, setArr, val) =>
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const reset = () => {
    setCat("all");
    setTypes([]);
    setFormats([]);
    setCovers([]);
    setFeatures([]);
    setPrice([0, 12000]);
  };

  const counts = useMemo(() => {
    const c = { all: products.length };
    CATS.forEach((x) => {
      if (x.key === "all") return;
      c[x.key] = products.filter((p) => p.category === x.key).length;
    });
    return c;
  }, [products]);

  const filtered = useMemo(() => {
    let list = products.filter((p) => p.status !== "hidden");
    if (cat !== "all") list = list.filter((p) => p.category === cat);
    if (types.length) list = list.filter((p) => types.includes(p.plannerType));
    if (formats.length) list = list.filter((p) => p.format.some((f) => formats.includes(f)));
    if (covers.length) list = list.filter((p) => p.cover.some((c) => covers.includes(c)));
    if (features.length) list = list.filter((p) => features.every((f) => p.features.includes(f)));
    list = list.filter((p) => p.priceINR >= price[0] && p.priceINR <= price[1]);
    if (sort === "price-asc") list.sort((a, b) => a.priceINR - b.priceINR);
    if (sort === "price-desc") list.sort((a, b) => b.priceINR - a.priceINR);
    if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [cat, types, formats, covers, features, price, sort, products]);

  const Filter = ({ title, children, defaultOpen = true }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
      <div className="py-5 border-b border-line">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center justify-between w-full overline"
        >
          {title}
          <ChevronDown size={12} className={`transition-transform ${open ? "" : "-rotate-90"}`} />
        </button>
        {open && <div className="mt-4 space-y-3">{children}</div>}
      </div>
    );
  };

  return (
    <div data-testid="shop-page">
      {/* Hero */}
      <section className="relative bg-ivory border-b border-line">
        <div className="container-nc py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex items-center gap-2 text-xs text-muted_brown mb-6 uppercase tracking-[0.2em]">
              <Link to="/" className="hover:text-espresso">Home</Link>
              <ChevronRight size={12} />
              <span className="text-espresso">Shop</span>
            </div>
            <h1 className="font-serif text-5xl md:text-6xl text-espresso leading-tight">
              Premium<br />Planners
            </h1>
            <p className="text-muted_brown mt-6 max-w-md">
              Curated planners for intentional living. Elegant design. Purposeful planning.
            </p>
            <Link to="#grid" className="btn-primary mt-8 inline-flex">Explore Collection</Link>
          </div>
          <img
            src={IMAGES.planner_closed}
            alt="Planner"
            className="rounded-2xl w-full aspect-[5/4] object-cover shadow-medium"
            loading="eager"
            fetchPriority="high"
          />
        </div>
      </section>

      {/* Main grid */}
      <section id="grid" className="container-nc py-14 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
        {/* Sidebar */}
        <aside
          data-testid="shop-filters"
          className="hidden lg:sticky lg:top-24 lg:block lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pr-3"
        >
          <Filter title="Categories">
            {CATS.map((c) => (
              <label key={c.key} className="flex items-center gap-3 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="cat"
                  data-testid={`filter-cat-${c.key}`}
                  checked={cat === c.key}
                  onChange={() => setCat(c.key)}
                  className="accent-espresso"
                />
                <span className={cat === c.key ? "text-espresso" : "text-muted_brown"}>{c.label}</span>
                <span className="ml-auto text-xs text-muted_brown">({counts[c.key] || 0})</span>
              </label>
            ))}
          </Filter>

          <Filter title="Planner Type">
            {TYPES.map((t) => (
              <label key={t} className="flex items-center gap-3 text-sm cursor-pointer capitalize">
                <Checkbox
                  checked={types.includes(t)}
                  onCheckedChange={() => toggle(types, setTypes, t)}
                  data-testid={`filter-type-${t}`}
                />
                <span className="text-muted_brown">{t === "mba" ? "MBA" : t}</span>
              </label>
            ))}
          </Filter>

          <Filter title="Format">
            {FORMATS.map((f) => (
              <label key={f} className="flex items-center gap-3 text-sm cursor-pointer">
                <Checkbox
                  checked={formats.includes(f)}
                  onCheckedChange={() => toggle(formats, setFormats, f)}
                  data-testid={`filter-format-${f}`}
                />
                <span className="text-muted_brown">{f}</span>
              </label>
            ))}
          </Filter>

          <Filter title="Cover">
            {COVERS.map((c) => (
              <label key={c} className="flex items-center gap-3 text-sm cursor-pointer">
                <Checkbox
                  checked={covers.includes(c)}
                  onCheckedChange={() => toggle(covers, setCovers, c)}
                  data-testid={`filter-cover-${c}`}
                />
                <span className="text-muted_brown">{c}</span>
              </label>
            ))}
          </Filter>

          <Filter title="Features">
            {FEATURES.map((f) => (
              <label key={f} className="flex items-center gap-3 text-sm cursor-pointer capitalize">
                <Checkbox
                  checked={features.includes(f)}
                  onCheckedChange={() => toggle(features, setFeatures, f)}
                  data-testid={`filter-feature-${f}`}
                />
                <span className="text-muted_brown">{f.replace("-", " ")}</span>
              </label>
            ))}
          </Filter>

          <Filter title="Price">
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-muted_brown">
                <span className="block mb-1 uppercase tracking-wider">Min</span>
                <input
                  type="number"
                  min={0}
                  max={12000}
                  step={100}
                  value={price[0]}
                  data-testid="filter-price-min"
                  onChange={(e) => {
                    const v = Math.max(0, Math.min(price[1] - 100, Number(e.target.value) || 0));
                    setPrice([v, price[1]]);
                  }}
                  className="input-nc bg-cream text-espresso py-2 text-sm"
                />
              </label>
              <label className="text-xs text-muted_brown">
                <span className="block mb-1 uppercase tracking-wider">Max</span>
                <input
                  type="number"
                  min={0}
                  max={12000}
                  step={100}
                  value={price[1]}
                  data-testid="filter-price-max"
                  onChange={(e) => {
                    const v = Math.min(12000, Math.max(price[0] + 100, Number(e.target.value) || 12000));
                    setPrice([price[0], v]);
                  }}
                  className="input-nc bg-cream text-espresso py-2 text-sm"
                />
              </label>
            </div>
            <div className="relative h-1 bg-line rounded-full mt-4">
              <div
                className="absolute h-full bg-espresso rounded-full"
                style={{
                  left: `${(price[0] / 12000) * 100}%`,
                  right: `${100 - (price[1] / 12000) * 100}%`,
                }}
              />
            </div>
            <div className="flex justify-between text-xs text-muted_brown mt-2">
              <span>INR {price[0]}</span>
              <span>INR {price[1]}</span>
            </div>
          </Filter>

          <button
            data-testid="reset-filters"
            onClick={reset}
            className="mt-6 w-full flex items-center justify-center gap-2 text-sm text-espresso border border-line rounded-full py-3 hover:bg-ivory transition-colors"
          >
            <RotateCcw size={14} /> Reset Filters
          </button>
        </aside>

        {/* Products */}
        <div>
          <div className="mb-6 lg:hidden">
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="flex-1 rounded-full border border-espresso bg-cream px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-espresso shadow-soft"
              >
                Filters
              </button>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="h-11 flex-1 rounded-full border-espresso bg-cream px-4 text-xs font-semibold uppercase tracking-[0.12em] text-espresso shadow-soft" data-testid="sort-select-mobile">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="price-asc">Price: Low to High</SelectItem>
                  <SelectItem value="price-desc">Price: High to Low</SelectItem>
                  <SelectItem value="rating">Highest Rated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
              {CATS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCat(c.key)}
                  data-testid={`mobile-filter-cat-${c.key}`}
                  className={`shrink-0 rounded-full border px-4 py-2 text-sm transition-colors ${
                    cat === c.key ? "border-espresso bg-espresso text-cream" : "border-line bg-cream text-espresso"
                  }`}
                >
                  {c.label}
                </button>
              ))}
              {TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggle(types, setTypes, t)}
                  data-testid={`mobile-filter-type-${t}`}
                  className={`shrink-0 rounded-full border px-4 py-2 text-sm capitalize transition-colors ${
                    types.includes(t) ? "border-espresso bg-espresso text-cream" : "border-line bg-cream text-espresso"
                  }`}
                >
                  {t === "mba" ? "MBA" : t}
                </button>
              ))}
              {FORMATS.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => toggle(formats, setFormats, f)}
                  data-testid={`mobile-filter-format-${f}`}
                  className={`shrink-0 rounded-full border px-4 py-2 text-sm transition-colors ${
                    formats.includes(f) ? "border-espresso bg-espresso text-cream" : "border-line bg-cream text-espresso"
                  }`}
                >
                  {f}
                </button>
              ))}
              {COVERS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggle(covers, setCovers, c)}
                  data-testid={`mobile-filter-cover-${c}`}
                  className={`shrink-0 rounded-full border px-4 py-2 text-sm transition-colors ${
                    covers.includes(c) ? "border-espresso bg-espresso text-cream" : "border-line bg-cream text-espresso"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="mt-3 flex items-center justify-between text-sm text-muted_brown">
              <span>Showing <span className="font-medium text-espresso">{filtered.length}</span> of {counts.all}</span>
              {(types.length || formats.length || covers.length || features.length || cat !== "all") ? (
                <button type="button" onClick={reset} className="text-espresso underline underline-offset-4">
                  Reset
                </button>
              ) : null}
            </div>
          </div>

          <div className="hidden lg:flex items-center justify-between mb-8 flex-wrap gap-3">
            <div className="text-sm text-muted_brown">
              Showing <span className="text-espresso font-medium">1–{filtered.length}</span> of {counts.all} products
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-52 border-line bg-cream rounded-full" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="featured">Sort by: Featured</SelectItem>
                <SelectItem value="price-asc">Price: Low to High</SelectItem>
                <SelectItem value="price-desc">Price: High to Low</SelectItem>
                <SelectItem value="rating">Highest Rated</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filtered.length === 0 ? (
            <div className="py-32 text-center text-muted_brown">
              No products match your filters.
            </div>
          ) : (
            <>
              {/* Featured bestseller "big box" */}
              {bestseller && (cat === "all" || cat === "planners") && filtered.some((p) => p.id === bestseller.id) && (
                <div
                  data-testid="featured-bestseller"
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(`/product/${bestseller.slug}`)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") navigate(`/product/${bestseller.slug}`);
                  }}
                  className="mb-12 rounded-2xl overflow-hidden border border-line bg-ivory grid grid-cols-1 lg:grid-cols-2 cursor-pointer"
                >
                  <div className="relative aspect-[5/4] lg:aspect-auto">
                    <img
                      src={bestseller.images[0]}
                      alt={bestseller.name}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    <span className="absolute top-4 left-4 bg-cream/95 backdrop-blur-sm text-espresso text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 rounded-full border border-line">
                      BESTSELLER
                    </span>
                  </div>
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <p className="overline mb-4">The Life OS Planner</p>
                    <h2 className="font-serif text-4xl md:text-5xl text-espresso leading-tight">
                      The planner for your everyday operating system.
                    </h2>
                    <p className="text-muted_brown mt-5 leading-relaxed">{bestseller.description}</p>
                    <ul className="mt-5 space-y-2">
                      {bestseller.bullets?.slice(0, 3).map((b) => (
                        <li key={b} className="flex items-start gap-2 text-sm text-espresso">
                          <Check size={14} className="text-gold mt-1" /> {b}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-7 flex items-center gap-1 text-sm text-muted_brown">
                      {[...Array(5)].map((_, i) => <Star key={i} size={13} className="fill-gold text-gold" strokeWidth={0} />)}
                      <span className="ml-1 text-espresso">4.9 rating</span>
                      <span>- 167 rating</span>
                    </div>
                    <div className="mt-6 flex items-center gap-4 flex-wrap">
                      <div>
                        <div className="flex items-center gap-5 whitespace-nowrap">
                          <span className="text-xs uppercase tracking-[0.22em] text-muted_brown">Starting Price</span>
                          <span className="text-3xl font-medium text-espresso leading-none">INR 349</span>
                        </div>
                        {bestseller.compareAtINR && (
                          <div className="text-xs text-muted_brown line-through">{formatINR(bestseller.compareAtINR)}</div>
                        )}
                      </div>
                      <Link to={`/product/${bestseller.slug}`} onClick={(event) => event.stopPropagation()} className="btn-primary" data-testid="featured-shop">
                        Shop the bestseller <ArrowRight size={16} />
                      </Link>
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          addItem(bestseller);
                        }}
                        data-testid="featured-quick-add"
                        className="btn-outline"
                      >
                        Quick add
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-14">
                {filtered.filter((p) => !(bestseller && (cat === "all" || cat === "planners") && p.id === bestseller.id)).map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
